import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Send, Sparkles, MessageSquare, AlertTriangle, User, Bot, ChevronRight,
    Mail, Phone, MessageCircle, ArrowUpRight, Tag, Filter, Search, Wand2,
    UserPlus, X, ThumbsUp, RefreshCw,
} from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { StatCard } from "@/components/ui/StatCard";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPost } from "@/lib/api";
import pt from "@/lib/i18n";

const CHANNEL_ICON = {
    web_chat: MessageCircle,
    whatsapp: MessageSquare,
    email: Mail,
    voice: Phone,
};
const CHANNEL_COLOR = {
    web_chat: "#2D6A8A",
    whatsapp: "#2D7A4F",
    email: "#C87941",
    voice: "#7A5A9C",
};
const STATUS_VARIANT = { active: "info", waiting: "warning", escalated: "error", closed: "neutral" };
const URGENCY_VARIANT = { baixa: "neutral", media: "info", alta: "warning", critica: "error" };
const SENTIMENT_COLOR = {
    muito_negativo: "#C0392B", negativo: "#E67E22", neutro: "#94A3B8",
    positivo: "#2D7A4F", muito_positivo: "#1F8049",
};

const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
};

const ConversationsPage = () => {
    const [stats, setStats] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState(null);
    const [search, setSearch] = useState("");
    const [channelFilter, setChannelFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [composer, setComposer] = useState("");
    const [triage, setTriage] = useState(null);
    const [triageLoading, setTriageLoading] = useState(false);
    const [draft, setDraft] = useState(null);
    const [draftLoading, setDraftLoading] = useState(false);
    const [tone, setTone] = useState("profissional");
    const [draftDisplay, setDraftDisplay] = useState("");
    const [loading, setLoading] = useState(true);
    const threadEndRef = useRef(null);

    const loadList = async () => {
        try {
            const [s, list] = await Promise.all([
                apiGet("/cx-stats"),
                apiGet("/conversations"),
            ]);
            setStats(s);
            setConversations(list || []);
            setLoading(false);
        } catch (e) { setLoading(false); }
    };

    const loadThread = async (id) => {
        if (!id) return;
        try {
            const r = await apiGet(`/conversations/${id}`);
            setThread(r);
            setTriage(r.conversation?.triage || null);
            setDraft(null);
            setDraftDisplay("");
            setComposer("");
            setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
        } catch (e) { setThread(null); }
    };

    useEffect(() => { loadList(); const id = setInterval(loadList, 20000); return () => clearInterval(id); }, []);
    const effectiveSelected = selected || (conversations.length ? conversations[0].id : null);
    useEffect(() => { if (effectiveSelected) loadThread(effectiveSelected); }, [effectiveSelected]);

    const filtered = useMemo(() => {
        const s = search.toLowerCase();
        return (conversations || []).filter((c) => {
            if (channelFilter && c.channel !== channelFilter) return false;
            if (statusFilter && c.status !== statusFilter) return false;
            if (!s) return true;
            const name = c.customer?.full_name?.toLowerCase() || "";
            const last = c.last_message?.content?.toLowerCase() || "";
            return name.includes(s) || last.includes(s);
        });
    }, [conversations, search, channelFilter, statusFilter]);

    const sendMessage = async () => {
        if (!composer.trim() || !thread) return;
        const content = composer.trim();
        setComposer("");
        try {
            await apiPost(`/conversations/${thread.conversation.id}/messages`, {
                content, sender_type: "agent",
            });
            await loadThread(thread.conversation.id);
            await loadList();
        } catch (e) { /* ignore */ }
    };

    const runTriage = async () => {
        if (!thread) return;
        setTriageLoading(true);
        try {
            const t = await apiPost(`/conversations/${thread.conversation.id}/triage`);
            setTriage(t);
        } catch (e) { /* ignore */ }
        setTriageLoading(false);
    };

    const generateDraft = async () => {
        if (!thread) return;
        setDraftLoading(true);
        setDraftDisplay("");
        try {
            const r = await apiPost(`/conversations/${thread.conversation.id}/draft-reply`, { tone });
            setDraft(r);
            // Typewriter effect
            const text = r.draft || "";
            let i = 0;
            const id = setInterval(() => {
                i += 3;
                setDraftDisplay(text.slice(0, i));
                if (i >= text.length) clearInterval(id);
            }, 18);
        } catch (e) { /* ignore */ }
        setDraftLoading(false);
    };

    const insertDraft = () => {
        if (draft?.draft) setComposer(draft.draft);
    };

    const escalate = async () => {
        if (!thread) return;
        try {
            await apiPost(`/conversations/${thread.conversation.id}/escalate`, {
                reason: "Pedido do agente",
            });
            await loadList();
            await loadThread(thread.conversation.id);
        } catch (e) { /* ignore */ }
    };

    const closeConv = async () => {
        if (!thread) return;
        const score = window.prompt("CSAT (1-5)?", "5");
        const csat = score ? parseInt(score, 10) : undefined;
        try {
            await apiPost(`/conversations/${thread.conversation.id}/close`, csat ? { csat_score: csat } : {});
            if (csat) {
                await apiPost("/surveys", {
                    conversation_id: thread.conversation.id,
                    customer_id: thread.customer?.id,
                    score: csat,
                });
            }
            await loadList();
            await loadThread(thread.conversation.id);
        } catch (e) { /* ignore */ }
    };

    return (
        <div data-testid="conversations-page" className="space-y-6">
            <PageHeader title={pt.conversations.title} subtitle={pt.conversations.subtitle} testId="conv-header" />

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label={pt.conversations.kpiOpen} value={stats?.open_conversations ?? 0} index={0} testId="kpi-open" />
                <StatCard label={pt.conversations.kpiEscalated} value={stats?.escalated ?? 0} index={1} testId="kpi-escalated" />
                <StatCard label={pt.conversations.kpiSentiment} value={Math.abs(stats?.avg_sentiment ?? 0) < 0.005 ? 0 : (stats?.avg_sentiment ?? 0)} index={2} testId="kpi-sentiment" />
                <StatCard label={pt.conversations.kpiCsat} value={stats?.avg_csat ?? 0} index={3} testId="kpi-csat" />
            </div>

            {/* 3-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: "70vh" }}>
                {/* Inbox column */}
                <div className="lg:col-span-3">
                    <GlassCard padding="p-0" className="overflow-hidden h-full flex flex-col">
                        <div className="p-4 border-b border-[color:var(--glass-border)]">
                            <p className="font-body text-xs uppercase tracking-[0.16em] text-[color:var(--slate-mid)] mb-2">{pt.conversations.inbox}</p>
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--slate-mid)]" />
                                <GlassInput
                                    placeholder={pt.conversations.searchPlaceholder}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-9"
                                    data-testid="conv-search-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <select
                                    value={channelFilter}
                                    onChange={(e) => setChannelFilter(e.target.value)}
                                    className="px-2 py-1.5 rounded-md bg-[color:var(--bg-surface)] border border-[color:var(--glass-border)] text-xs font-body text-[color:var(--slate-deep)]"
                                    data-testid="conv-channel-filter"
                                >
                                    <option value="">{pt.conversations.filterAll}</option>
                                    {Object.entries(pt.conversations.channels).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="px-2 py-1.5 rounded-md bg-[color:var(--bg-surface)] border border-[color:var(--glass-border)] text-xs font-body text-[color:var(--slate-deep)]"
                                    data-testid="conv-status-filter"
                                >
                                    <option value="">{pt.conversations.filterStatus}</option>
                                    {Object.entries(pt.conversations.statuses).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto" data-testid="conv-list">
                            {loading ? (
                                <div className="p-4 space-y-3">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16" />)}</div>
                            ) : filtered.length === 0 ? (
                                <p className="p-6 text-center font-body text-sm text-[color:var(--slate-mid)]">{pt.conversations.empty}</p>
                            ) : (
                                filtered.map((c) => {
                                    const Icon = CHANNEL_ICON[c.channel] || MessageCircle;
                                    const isSelected = effectiveSelected === c.id;
                                    return (
                                        <button
                                            key={c.id}
                                            onClick={() => setSelected(c.id)}
                                            className={"w-full text-left p-3 border-b border-[color:var(--glass-border)] transition-all " + (isSelected ? "bg-[color:var(--copper-subtle)]" : "hover:bg-[color:var(--bg-surface)]")}
                                            style={isSelected ? { borderLeft: "3px solid var(--copper)" } : { borderLeft: "3px solid transparent" }}
                                            data-testid={`conv-item-${c.id}`}
                                        >
                                            <div className="flex items-start gap-2.5">
                                                <div className="rounded-md p-1.5" style={{ background: `${CHANNEL_COLOR[c.channel]}22`, color: CHANNEL_COLOR[c.channel] }}>
                                                    <Icon size={14} strokeWidth={1.8} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="font-body text-sm font-medium text-[color:var(--slate-deep)] truncate">
                                                            {c.customer?.full_name || pt.convExtra?.anonymousCustomer || "Cliente Anónimo"}
                                                        </p>
                                                        <span className="font-mono text-[10px] text-[color:var(--slate-mid)]">{fmtTime(c.updated_at)}</span>
                                                    </div>
                                                    <p className="font-body text-xs text-[color:var(--slate-mid)] truncate mt-0.5">
                                                        {c.last_message?.content || "—"}
                                                    </p>
                                                    <div className="flex items-center gap-1.5 mt-1.5">
                                                        <NxBadge variant={STATUS_VARIANT[c.status]}>{pt.conversations.statuses[c.status]}</NxBadge>
                                                        {c.message_count > 0 && (
                                                            <span className="font-mono text-[10px] text-[color:var(--slate-mid)]">{c.message_count} msg</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </GlassCard>
                </div>

                {/* Thread column */}
                <div className="lg:col-span-6">
                    <GlassCard padding="p-0" className="overflow-hidden h-full flex flex-col">
                        {!thread ? (
                            <div className="flex-1 flex items-center justify-center">
                                <p className="font-body text-sm text-[color:var(--slate-mid)]">{pt.conversations.emptyThread}</p>
                            </div>
                        ) : (
                            <>
                                {/* Header */}
                                <div className="p-4 border-b border-[color:var(--glass-border)] flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <NxAvatar name={thread.customer?.full_name || "?"} size="md" />
                                        <div>
                                            <p className="font-body font-medium text-[color:var(--slate-deep)]">
                                                {thread.customer?.full_name || pt.convExtra?.anonymousCustomer || "Cliente Anónimo"}
                                            </p>
                                            <p className="font-mono text-[11px] text-[color:var(--slate-mid)]">
                                                {pt.conversations.channels[thread.conversation.channel]} · {pt.conversations.statuses[thread.conversation.status]}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {thread.conversation.status !== "escalated" && thread.conversation.status !== "closed" && (
                                            <NxButton variant="secondary" size="sm" onClick={escalate} data-testid="btn-escalate" icon={<UserPlus size={14} />}>
                                                {pt.conversations.escalate}
                                            </NxButton>
                                        )}
                                        {thread.conversation.status !== "closed" && (
                                            <NxButton variant="secondary" size="sm" onClick={closeConv} data-testid="btn-close" icon={<X size={14} />}>
                                                {pt.conversations.close}
                                            </NxButton>
                                        )}
                                    </div>
                                </div>

                                {/* Messages */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="thread-messages" style={{ background: "linear-gradient(180deg, rgba(248,243,235,0.4), rgba(248,243,235,0))" }}>
                                    <AnimatePresence initial={false}>
                                        {(thread.messages || []).map((m) => {
                                            const isCustomer = m.sender_type === "customer";
                                            const isBot = m.sender_type === "ai_bot";
                                            return (
                                                <motion.div
                                                    key={m.id}
                                                    initial={{ opacity: 0, y: 8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className={"flex gap-2 " + (isCustomer ? "justify-start" : "justify-end")}
                                                >
                                                    {isCustomer && <NxAvatar name={thread.customer?.full_name || "?"} size="sm" />}
                                                    <div
                                                        className={"max-w-[70%] rounded-2xl px-3.5 py-2 font-body text-sm " + (isCustomer ? "bg-white/80 text-[color:var(--slate-deep)] border border-[color:var(--glass-border)]" : "bg-gradient-to-br from-[color:var(--copper-light)] to-[color:var(--copper)] text-white")}
                                                        style={isBot && !isCustomer ? { background: "linear-gradient(135deg, #5C7A92 0%, #2D6A8A 100%)" } : {}}
                                                    >
                                                        {!isCustomer && (
                                                            <p className="font-mono text-[10px] opacity-80 mb-1 flex items-center gap-1">
                                                                {isBot ? <Bot size={10} /> : <User size={10} />}
                                                                {isBot ? "Assistente IA" : "Agente"}
                                                            </p>
                                                        )}
                                                        <p className="leading-relaxed whitespace-pre-wrap">{m.content}</p>
                                                        <p className={"font-mono text-[10px] mt-1 " + (isCustomer ? "text-[color:var(--slate-mid)]" : "opacity-70")}>
                                                            {fmtTime(m.created_at)}
                                                        </p>
                                                    </div>
                                                    {!isCustomer && (
                                                        <div className="rounded-full p-1.5 self-end" style={{ background: isBot ? "rgba(45,106,138,0.15)" : "var(--copper-subtle)", color: isBot ? "#2D6A8A" : "var(--copper)" }}>
                                                            {isBot ? <Bot size={14} /> : <User size={14} />}
                                                        </div>
                                                    )}
                                                </motion.div>
                                            );
                                        })}
                                    </AnimatePresence>
                                    <div ref={threadEndRef} />
                                </div>

                                {/* Composer */}
                                {thread.conversation.status !== "closed" && (
                                    <div className="p-3 border-t border-[color:var(--glass-border)] bg-[color:var(--bg-surface)]">
                                        <div className="flex gap-2">
                                            <textarea
                                                rows={2}
                                                value={composer}
                                                onChange={(e) => setComposer(e.target.value)}
                                                placeholder={pt.conversations.composerPlaceholder}
                                                className="flex-1 px-3 py-2 rounded-lg bg-white/80 border border-[color:var(--glass-border)] font-body text-sm text-[color:var(--slate-deep)] focus:outline-none focus:border-[color:var(--copper)] resize-none"
                                                data-testid="composer-input"
                                                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendMessage(); }}
                                            />
                                            <NxButton onClick={sendMessage} disabled={!composer.trim()} icon={<Send size={14} />} data-testid="btn-send-message">
                                                {pt.conversations.send}
                                            </NxButton>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </GlassCard>
                </div>

                {/* AI Sidebar */}
                <div className="lg:col-span-3 space-y-4">
                    {/* Triage */}
                    <GlassCard padding="p-4" data-testid="triage-card">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Sparkles size={14} className="text-[color:var(--copper)]" />
                                <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)]">{pt.conversations.triage}</p>
                            </div>
                            <NxButton variant="secondary" size="sm" onClick={runTriage} disabled={triageLoading || !thread} data-testid="btn-triage" icon={<Wand2 size={12} />}>
                                {triageLoading ? "…" : pt.conversations.triageRun}
                            </NxButton>
                        </div>
                        {!triage ? (
                            <p className="font-body text-xs text-[color:var(--slate-mid)] italic">
                                Carregue em &ldquo;Classificar com IA&rdquo; para analisar a conversa.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="p-2 rounded-md bg-[color:var(--bg-surface)] border border-[color:var(--glass-border)]">
                                        <p className="font-mono text-[10px] text-[color:var(--slate-mid)] uppercase">{pt.conversations.triageUrgency}</p>
                                        <NxBadge variant={URGENCY_VARIANT[triage.urgency] || "neutral"}>{pt.conversations.urgency[triage.urgency] || triage.urgency}</NxBadge>
                                    </div>
                                    <div className="p-2 rounded-md bg-[color:var(--bg-surface)] border border-[color:var(--glass-border)]">
                                        <p className="font-mono text-[10px] text-[color:var(--slate-mid)] uppercase">{pt.conversations.triageIntent}</p>
                                        <p className="font-body text-xs font-medium text-[color:var(--slate-deep)] mt-0.5">
                                            {pt.conversations.intent[triage.intent] || triage.intent}
                                        </p>
                                    </div>
                                </div>
                                <div className="p-2 rounded-md bg-[color:var(--bg-surface)] border border-[color:var(--glass-border)]">
                                    <p className="font-mono text-[10px] text-[color:var(--slate-mid)] uppercase mb-1">{pt.conversations.triageSentiment}</p>
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm" style={{ color: SENTIMENT_COLOR[triage.sentiment_label] }}>
                                            {pt.conversations.sentiment[triage.sentiment_label] || triage.sentiment_label}
                                        </span>
                                        <span className="font-mono text-[10px] text-[color:var(--slate-mid)]">({triage.sentiment_score})</span>
                                    </div>
                                </div>
                                {triage.summary && (
                                    <div>
                                        <p className="font-mono text-[10px] text-[color:var(--slate-mid)] uppercase mb-1">{pt.conversations.triageSummary}</p>
                                        <p className="font-body text-xs text-[color:var(--slate-deep)] leading-relaxed italic">&ldquo;{triage.summary}&rdquo;</p>
                                    </div>
                                )}
                                {triage.suggested_actions?.length > 0 && (
                                    <div>
                                        <p className="font-mono text-[10px] text-[color:var(--slate-mid)] uppercase mb-1">{pt.conversations.triageActions}</p>
                                        <ul className="space-y-1">
                                            {triage.suggested_actions.map((a, i) => (
                                                <li key={i} className="font-body text-xs text-[color:var(--slate-deep)] flex items-start gap-1.5">
                                                    <ChevronRight size={11} className="mt-0.5 text-[color:var(--copper)] shrink-0" />
                                                    <span>{a}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {triage.suggested_kb_tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {triage.suggested_kb_tags.map((t, i) => (
                                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--copper-subtle)] text-[color:var(--copper-dark)] font-mono text-[10px]">
                                                <Tag size={9} />{t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </GlassCard>

                    {/* Draft reply */}
                    <GlassCard padding="p-4" data-testid="draft-card">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Sparkles size={14} className="text-[color:var(--copper)]" />
                                <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)]">{pt.conversations.draft}</p>
                            </div>
                        </div>
                        <div className="flex gap-1.5 mb-3">
                            {["profissional", "empatico", "formal"].map((t) => (
                                <button
                                    key={t}
                                    onClick={() => setTone(t)}
                                    className={"px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-wide transition-all " + (tone === t ? "bg-[color:var(--copper)] text-white" : "bg-[color:var(--bg-surface)] text-[color:var(--slate-mid)] border border-[color:var(--glass-border)]")}
                                    data-testid={`tone-${t}`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                        <NxButton
                            onClick={generateDraft}
                            disabled={draftLoading || !thread || thread?.conversation?.status === "closed"}
                            className="w-full mb-2"
                            size="sm"
                            icon={<RefreshCw size={12} className={draftLoading ? "animate-spin" : ""} />}
                            data-testid="btn-draft"
                        >
                            {draftLoading ? "A gerar…" : (draft ? pt.conversations.draftRegenerate : pt.conversations.draftGenerate)}
                        </NxButton>
                        {draft && (
                            <>
                                <div className="p-2.5 rounded-md bg-[color:var(--bg-surface)] border border-[color:var(--copper-subtle)] mb-2 min-h-[60px]">
                                    <p className="font-body text-xs text-[color:var(--slate-deep)] leading-relaxed whitespace-pre-wrap">
                                        {draftDisplay}
                                        {draftLoading && <span className="inline-block w-1 h-3 bg-[color:var(--copper)] ml-0.5 animate-pulse" />}
                                    </p>
                                </div>
                                <NxButton variant="secondary" size="sm" onClick={insertDraft} className="w-full" data-testid="btn-insert-draft" icon={<ArrowUpRight size={12} />}>
                                    {pt.conversations.draftInsert}
                                </NxButton>
                            </>
                        )}
                    </GlassCard>

                    {/* CSAT badge if closed */}
                    {thread?.conversation?.status === "closed" && thread?.conversation?.csat_score && (
                        <GlassCard padding="p-4">
                            <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)] mb-2">{pt.conversations.csat}</p>
                            <div className="flex items-center gap-2">
                                <ThumbsUp size={16} className="text-[color:var(--success)]" />
                                <span className="font-mono text-2xl text-[color:var(--copper-dark)]">{thread.conversation.csat_score}/5</span>
                            </div>
                        </GlassCard>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConversationsPage;
